import { useReveal } from '../../hooks/useReveal';
import React, { useEffect, useRef, useState } from 'react';
import { useSocialStore } from '../../stores/socialStore';
import { useWhatsNewStore } from '../../stores/whatsNewStore';
import { useGiftStore } from '../../stores/giftStore';
import { useUnreadNotificationCount } from '../../hooks/useVisibleNotifications';
import { NotificationPanel } from '../views/SocialView/NotificationPanel';
import { AvatarProgressRing } from './AvatarProgressRing';
import { HeaderAvatarReward } from './HeaderAvatarReward';

/**
 * The top-right avatar is the app's ACCOUNT MENU — and the global notification
 * centre with it. Its badge counts unread notifications (the level lives on the
 * rail/progress surfaces) and clicking opens a menu from anywhere in the app.
 *
 * ⭐ SETTINGS LEFT THIS MENU (owner, 2026-09-29) — it is pinned to the foot of
 * the nav rail now, and in the phone's drawer. What is left is the feed.
 * ⚠️ So the menu has NOTHING IN IT until notifications arrive (step 13): the
 * avatar does not open an empty box before then.
 *
 * This component owns the app-wide fetch + realtime subscription.
 */
export function HeaderNotifications({ showMobileRing }: { showMobileRing: boolean }) {
  const { fetchNotifications, subscribeNotifications } = useSocialStore();
  const unreadCount = useUnreadNotificationCount();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const revealed = useReveal();
  const hasMenu = revealed('menu:notifications');

  useEffect(() => {
    fetchNotifications();
    // What's new: this build's release notes + the account's read marker
    void useWhatsNewStore.getState().load();
    // gifts waiting for an answer (loaded apart from the feed — see giftStore)
    void useGiftStore.getState().load();
    // ⚠️ The subscription resolves AFTER an await, so a cleanup that runs
    // first (StrictMode, a fast remount) aborts it: the store then creates no
    // channel at all. Removing one instead could leave realtime with zero
    // channels, which DISCONNECTS the socket for every other subscriber.
    const controller = new AbortController();
    let unsubscribe: (() => void) | undefined;
    subscribeNotifications(controller.signal).then(fn => {
      if (controller.signal.aborted) fn();
      else unsubscribe = fn;
    });
    return () => {
      controller.abort();
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const toggle = () => {
    if (!hasMenu) return;
    // ⚠️ Opening reads nothing (per-item read, 2026-10-04) — the badge only
    // falls as items are opened or "Mark all read" is pressed.
    setOpen(!open);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="lg:hidden">
        <AvatarProgressRing
          size={40}
          showRing={showMobileRing}
          showLevel={false}
          onClick={toggle}
          title="Notifications"
          ariaLabel={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
        />
      </div>
      <HeaderAvatarReward onClick={toggle} />

      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 z-10 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-card pointer-events-none">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}

      {open && hasMenu && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[min(24rem,calc(100vw_-_1.5rem))] overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          {/* ⚠️ The panel positions itself; inside the menu it must sit in the
              flow instead, so it is given `embedded` rather than wrapped. */}
          <NotificationPanel embedded onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
